import * as React from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import { useEffect } from "react";
import { marketCharts } from "../../features/user/userSlice";
import Loader from "../../components/common/Loader";

// --- Theme Colors and Constants ---
const PRIMARY_TEXT_COLOR = "#2D3A27"; // Dark green/text color
const BAR_COLOR = "#4CAF50"; // Green (Success/Tonnes)
const LINE_COLOR = "#F4C542"; // Yellow/Gold (Warning/Buyers)

const Y_AXIS_TONNES_ID = "tonnesAxis";
const Y_AXIS_BUYERS_ID = "buyersAxis";

// --- Inline SVG Icons (Lucide) for Tooltip ---
// Tonnes (BarChart icon) - represents volume
const TonnesIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block mr-1 align-middle"
  >
    <path d="M12 20V10" />
    <path d="M18 20V4" />
    <path d="M6 20v-4" />
    <line x1="1" x2="23" y1="20" y2="20" stroke="#f0f0f0" strokeWidth="1" />
  </svg>
);

// Buyers (Users icon) - represents count of people
const BuyersIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block mr-1 align-middle"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

// Custom styles for MUI Tabs to match the modern pill look
const customTabStyle = (activeColor, primaryTextColor) => ({
  color: primaryTextColor,
  fontWeight: 600,
  fontSize: "0.9rem",
  padding: "8px 18px", // Pill size
  minHeight: "40px",
  minWidth: "100px",
  margin: "0 4px",
  borderRadius: "9999px", // Full pill shape
  textTransform: "none",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  opacity: 0.7,
  "&.Mui-selected": {
    color: PRIMARY_TEXT_COLOR,
    fontWeight: 700,
    backgroundColor: activeColor, // Yellow/Gold background
    boxShadow: `0 2px 8px ${activeColor}80`,
    opacity: 1,
  },
  "&:hover": {
    backgroundColor: activeColor + "30", // Lighter hover effect
    opacity: 1,
  },
});

function TradingChartComponent() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [timeframe, setTimeframe] = React.useState("daily");
  const { chartData, chartLoading, chartError } = useSelector(
    (state) => state.user
  );

  useEffect(() => {
    const typeMap = { daily: "daily", weekly: "weekly", monthly: "monthly" };
    dispatch(marketCharts({ months: 12, type: typeMap[timeframe] }));
  }, [dispatch, timeframe]);

  // Get ordinal suffix (st, nd, rd, th)
  function getOrdinal(day) {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  // Format a Date object into "1st Oct 2025"
  function formatFullDate(date) {
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" }); // "Oct"
    const year = date.getFullYear();
    return `${day}${getOrdinal(day)} ${month} ${year}`;
  }

  function getDateOfISOWeek(week, year) {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    return ISOweekStart;
  }

  // Format Date object into "Aug 2025" (no day)
  function formatMonthYear(date) {
    const month = date.toLocaleString("default", { month: "short" }); // "Aug"
    const year = date.getFullYear();
    return `${month} ${year}`;
  }

  // Prepare data for Recharts
  const currentData = useMemo(() => {
    if (!chartData || !chartData.data) return [];

    switch (timeframe) {
      case "daily":
        return (
          chartData.data.daily?.labels?.map((label, idx) => {
            const dateObj = new Date(label);
            return {
              date: formatFullDate(dateObj),
              tonnes: chartData.data.daily.mtData?.[idx],
              buyers: chartData.data.daily.buyerData?.[idx],
            };
          }) || []
        );

      case "weekly":
        return (
          chartData.data.weekly?.labels?.map((label, idx) => {
            const [year, week] = label.split("-").map(Number);
            const weekDate = getDateOfISOWeek(week, year);
            return {
              week_label: formatFullDate(weekDate),
              tonnes: chartData.data.weekly.mtData?.[idx],
              buyers: chartData.data.weekly.buyerData?.[idx],
            };
          }) || []
        );

      case "monthly":
        return (
          chartData.data.monthly?.labels?.map((label, idx) => {
            const [year, month] = label.split("-").map(Number);
            const dateObj = new Date(year, month - 1, 1); // First day of month
            return {
              month_label: formatMonthYear(dateObj),
              tonnes: chartData.data.monthly.mtData?.[idx],
              buyers: chartData.data.monthly.buyerData?.[idx],
            };
          }) || []
        );

      default:
        return [];
    }
  }, [chartData, timeframe]);

  const xAxisKey = React.useMemo(() => {
    switch (timeframe) {
      case "daily":
        return "date";
      case "weekly":
        return "week_label";
      case "monthly":
        return "month_label";
      default:
        return "date";
    }
  }, [timeframe]);

  // Dynamic bar sizing logic
  const numDataPoints = currentData.length;
  const MAX_BAR_SIZE = 40;
  const barSize = Math.min(MAX_BAR_SIZE, 500 / numDataPoints);
  const barPadding = numDataPoints < 10 ? 30 : 15;

  // Data formatters for Tooltip and Y-Axis
  const formatTonnes = (v) => `${(v / 1000).toFixed(1)}K`;
  const formatBuyers = (v) => v.toLocaleString();
  const formatLargeNumbers = (v) =>
    v > 10000 ? `${(v / 1000).toFixed(0)}k` : v.toLocaleString();

  // Data formatters for Tooltip and Y-Axis (Kept for Tooltip, but removed from YAxis props)
  const CustomTooltip = ({ active, payload, label }) => {
    // Since the label is now pre-formatted in the data array, we return it directly.
    const displayLabel = label;

    if (active && payload && payload.length) {
      const tonnesData = payload.find((p) => p.dataKey === "tonnes");
      const buyersData = payload.find((p) => p.dataKey === "buyers");

      return (
        <Box
          sx={{
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(5px)",
            color: "white",
            p: 1.5,
            borderRadius: "12px",
            boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
            minWidth: "150px",
          }}
        >
          {/* Display the pre-formatted label from the data array */}
          <p className="font-bold mb-1 text-base">{displayLabel}</p>
          {tonnesData && (
            <p className="text-sm font-medium">
              <TonnesIcon color={BAR_COLOR} /> Tonnes:{" "}
              <span className="font-bold">
                {formatTonnes(tonnesData.value)}
              </span>
            </p>
          )}
          {buyersData && (
            <p className="text-sm font-medium">
              <BuyersIcon color={LINE_COLOR} /> Buyers:{" "}
              <span className="font-bold">
                {formatBuyers(buyersData.value)}
              </span>
            </p>
          )}
        </Box>
      );
    }
    return null;
  };

  // if (chartLoading)
  //   return (
  //     <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
  //       <CircularProgress />
  //     </Box>
  //   );
  // if (chartError) return <Typography color="error">{chartError}</Typography>;
  // if (!currentData.length)
  //   return <Typography>No data available for this timeframe.</Typography>;

  return (
    <Box>
      {/* 3. Outer Header */}
      <Typography
        variant="h3"
        sx={{
          color: PRIMARY_TEXT_COLOR,
          fontWeight: 900,
          mb: 6,
          fontSize: { xs: "3rem", md: "4rem" },
          textAlign: "center",
          textShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        Commodity Exchange Performance
      </Typography>

      {/* Main Card Container */}
      <Box
        sx={{
          py: { xs: 5, md: 8 },
          px: { xs: 4, md: 8 },
          mb: 8,
          position: "relative",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: "24px",
          border: `1px solid #E0E0E0`,
          boxShadow: `0 20px 50px ${PRIMARY_TEXT_COLOR}20`,
          maxWidth: "1300px",
          mx: "auto",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* 3. Inner Subtitle */}
        <Typography
          variant="h6"
          sx={{
            color: PRIMARY_TEXT_COLOR,
            fontWeight: 700,
            mb: 1,
            fontSize: "1.5rem",
          }}
        >
          Daily Trading Metrics Overview
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
            mb: 4,
            fontSize: "1rem",
          }}
        >
          Dual-axis analysis showing volume (Tonnes) and market engagement
          (Buyers) by time period.
        </Typography>

        {/* 2. Tabs with modern pill design */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "center" },
            mb: 6,
            p: 1,
            borderRadius: "16px",
            backgroundColor: "#F7F7F7",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
            overflowX: "auto",
          }}
        >
          <Tabs
            value={timeframe}
            onChange={(e, newValue) => setTimeframe(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            // Hide the default indicator line for the pill look
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              "& .MuiTabs-flexContainer": {
                flexWrap: { xs: "nowrap", md: "wrap" },
              },
            }}
          >
            <Tab
              label="Daily"
              value="daily"
              sx={customTabStyle(LINE_COLOR, PRIMARY_TEXT_COLOR)}
            />
            <Tab
              label="Weekly"
              value="weekly"
              sx={customTabStyle(LINE_COLOR, PRIMARY_TEXT_COLOR)}
            />
            <Tab
              label="Monthly"
              value="monthly"
              sx={customTabStyle(LINE_COLOR, PRIMARY_TEXT_COLOR)}
            />
          </Tabs>
        </Box>

        <Box height={400} className="w-full">
          {chartLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.6)",
                zIndex: 10,
              }}
            >
              {/* <CircularProgress /> */}
              <Loader/>
            </Box>
          )}

          {!chartLoading && currentData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={currentData}
              margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="5 5"
                stroke="#E0E0E0"
                vertical={false}
              />

              {/* X-Axis: Removed tick labels and related positioning props */}
              <XAxis
                dataKey={xAxisKey}
                scale="band"
                padding={{ left: barPadding, right: barPadding }}
                tick={false} // Hides all tick labels/dates
                tickLine={false}
                axisLine={{ stroke: "#999" }}
                stroke="#666"
              />

              {/* Primary Y-Axis (Left) for Tonnes (Bar) - Removed tick labels */}
              <YAxis
                yAxisId={Y_AXIS_TONNES_ID}
                orientation="left"
                stroke={BAR_COLOR}
                label={{
                  value: "Tonnes Traded",
                  angle: -90,
                  position: "insideLeft",
                  fill: BAR_COLOR,
                  dx: 0,
                  fontWeight: 600,
                  fontSize: "14px",
                }}
                tick={false} // Hides all tick numbers
                tickLine={false}
                axisLine={false}
                domain={["auto", "auto"]}
              />

              {/* Secondary Y-Axis (Right) for Buyers (Line) - Removed tick labels */}
              <YAxis
                yAxisId={Y_AXIS_BUYERS_ID}
                orientation="right"
                stroke={LINE_COLOR}
                label={{
                  value: "Number of Buyers",
                  angle: 90,
                  position: "insideRight",
                  fill: LINE_COLOR,
                  dx: 10,
                  fontWeight: 600,
                  fontSize: "14px",
                }}
                tick={false} // Hides all tick numbers
                tickLine={false}
                axisLine={false}
                domain={["auto", "auto"]}
              />

              {/* Custom Tooltip allows data inspection on hover */}
              <Tooltip content={<CustomTooltip />} />

              <Legend
                wrapperStyle={{
                  paddingTop: 15,
                  color: PRIMARY_TEXT_COLOR,
                  fontWeight: 500,
                  fontSize: "0.9rem",
                }}
              />

              {/* Bar Plot (Tonnes) */}
              <Bar
                yAxisId={Y_AXIS_TONNES_ID}
                dataKey="tonnes"
                name="Tonnes Traded"
                fill={BAR_COLOR}
                radius={[6, 6, 0, 0]}
                barSize={barSize}
                opacity={0.8} // Slightly transparent bars for clarity
              />

              {/* Line Plot (Buyers) - Drawn after the bar to ensure it is visually on top */}
              <Line
                yAxisId={Y_AXIS_BUYERS_ID}
                dataKey="buyers"
                name="Number of Buyers"
                stroke={LINE_COLOR}
                dot={{ stroke: LINE_COLOR, fill: "#fff", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 7, strokeWidth: 4, stroke: LINE_COLOR }}
                strokeWidth={3}
                type="monotone"
              />
            </ComposedChart>
          </ResponsiveContainer>
          ) : (
    // Show message only if not loading AND no data
    !chartLoading && (
      <Typography
        sx={{ textAlign: "center", py: 16, fontSize: "1.25rem" }}
        color="text.secondary"
      >
        No data available for this timeframe.
      </Typography>
    )
  )}
        </Box>
      </Box>
    </Box>
  );
}

// Set up the App component wrapper for context
const App = () => (
  // Tailwind classes for body/page background and overall font
  <Box sx={{ minHeight: "100vh", backgroundColor: "#FFF", p: 4 }}>
    <TradingChartComponent />
  </Box>
);

export default App;
